import { useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'flowbite-react';
import ButtonWrapper from '../../../components/button/button';
import { searchKnowledge } from '../../../services/knowledge';

function formatAnswer(response) {
  if (response == null) return '';
  if (typeof response === 'string') return response;
  // Common shapes the search/extraction function may return.
  if (response.message) return response.message;
  if (response.answer) return response.answer;
  if (response.result) {
    return typeof response.result === 'string'
      ? response.result
      : JSON.stringify(response.result, null, 2);
  }
  return JSON.stringify(response, null, 2);
}

export default function TestModal({ appId, dataSource, onClose }) {
  // The query field is uncontrolled (ref-backed) so typing does not trigger a
  // re-render. That keeps flowbite's Modal focus-trap from re-running and
  // stealing focus out of the textarea on every keystroke.
  const queryRef = useRef(null);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (loading) return;
    // Validate only on click, so the Search button can stay always-enabled.
    const query = queryRef.current?.value.trim() || '';
    if (!query) {
      setAnswer('');
      setError('Please enter a query.');
      return;
    }
    // Clear any existing result before running a new query.
    setAnswer('');
    setError('');
    setLoading(true);
    try {
      const { response, status } = await searchKnowledge(appId, dataSource.dataSourceId, query);
      if (status === 200) {
        setAnswer(formatAnswer(response));
      } else {
        throw new Error(typeof response === 'string' ? response : 'Search failed');
      }
    } catch (e) {
      setError(e.message || 'Something went wrong while searching.');
    } finally {
      setLoading(false);
    }
  };

  const handleClean = () => {
    if (queryRef.current) {
      queryRef.current.value = '';
    }
    setAnswer('');
    setError('');
  };

  return (
    <Modal show={true} size="2xl" onClose={onClose}>
      <Modal.Header>Test Data Source</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Query
            </label>
            <textarea
              ref={queryRef}
              defaultValue=""
              placeholder="Enter your query..."
              rows={3}
              disabled={loading}
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Answer
            </label>
            <div className="min-h-[8rem] w-full whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              {loading ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Spinner size="sm" />
                  <span>Searching...</span>
                </div>
              ) : error ? (
                <span className="text-red-600">{error}</span>
              ) : answer ? (
                answer
              ) : (
                <span className="text-gray-400">The answer will appear here.</span>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full justify-end gap-3">
          <Button color="gray" onClick={handleClean} disabled={loading}>
            Clean
          </Button>
          <ButtonWrapper
            text="Search"
            onClick={handleSearch}
            loading={loading}
            disabled={loading}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}
